import React, { useState, useEffect } from 'react';
import {
    assignCourseToUserAsync,
    removeCourseFromUserAsync,
    populateCourseDropdownAsync,
    getAvailableCoursesForUserAsync
} from '../../../Services/CourseService';
import { showToast } from '../../../Infrastructure/Notifications/ToastrConfig';
import Modal from '../../../Shared/Components/Modal/Modal';

const UserCourseAssignments = ({ userId, userName }) => {
    const [allCourses, setAllCourses] = useState([]);
    const [availableCourses, setAvailableCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState('');
    const [courseToRemove, setCourseToRemove] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            // Fetch all courses for dropdown
            const coursesResult = await populateCourseDropdownAsync();
            setAllCourses(coursesResult.courses || []);

            // Fetch user's available courses
            const userCoursesResult = await getAvailableCoursesForUserAsync(userId);
            setAvailableCourses(userCoursesResult.courses || []);
        } catch (error) {
            console.error('Error fetching courses:', error);
            showToast(false, 'Failed to load courses');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (userId) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const handleAssignCourse = async () => {
        if (!selectedCourseId) {
            showToast(false, 'Please select a course');
            return;
        }

        try {
            const response = await assignCourseToUserAsync({
                userId: userId,
                courseId: selectedCourseId
            });

            if (response.success) {
                showToast(true, 'Course assigned successfully');
                setSelectedCourseId('');
                fetchData(); // Refresh the list
            } else {
                showToast(false, response.errors?.[0]?.error || 'Failed to assign course');
            }
        } catch (error) {
            console.error('Error assigning course:', error);
            showToast(false, 'Failed to assign course');
        }
    };

    const handleRemoveCourse = async () => {
        try {
            const response = await removeCourseFromUserAsync({
                userId: userId,
                courseId: courseToRemove.id
            });

            if (response.success) {
                showToast(true, 'Course removed successfully');
                fetchData(); // Refresh the list
            } else {
                showToast(false, 'Failed to remove course');
            }
        } catch (error) {
            console.error('Error removing course:', error);
            showToast(false, 'Failed to remove course');
        }
    };

    const getSourceBadgeColor = (source) => {
        switch (source) {
            case 'Direct':
                return '#8b5cf6'; // Purple
            case 'Group':
                return '#3b82f6'; // Blue
            case 'Client':
                return '#10b981'; // Green
            case 'Pathway':
                return '#f59e0b'; // Amber
            default:
                return '#6b7280'; // Gray
        }
    };

    const getSourceIcon = (source) => {
        switch (source) {
            case 'Direct':
                return '👤';
            case 'Group':
                return '👥';
            case 'Client':
                return '🏢';
            case 'Pathway':
                return '🎓';
            default:
                return '📚';
        }
    };

    return (
        <>
            <Modal
                modalId="removeUserCourseModal"
                bodyText={`Are you sure you want to remove "${courseToRemove.title}" from ${userName}?`}
                buttonFunc={handleRemoveCourse}
                buttonText="Remove"
                title="Remove Course Assignment"
            />

            <div>
                {/* Assign Course Section */}
                <div style={{
                    background: 'linear-gradient(135deg, #f6f8fb 0%, #ffffff 100%)',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '2px solid #e5e7eb',
                    marginBottom: '24px'
                }}>
                    <label style={{
                        display: 'block',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        color: '#64748b',
                        marginBottom: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        Assign Individual Course
                    </label>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <select
                            value={selectedCourseId}
                            onChange={(e) => setSelectedCourseId(e.target.value)}
                            className="form-control"
                            style={{
                                flex: '1',
                                padding: '12px 16px',
                                fontSize: '1rem',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                height: '48px'
                            }}
                        >
                            <option value=''>Select course...</option>
                            {allCourses.map(course => (
                                <option key={course.id} value={course.id}>{course.title} ({course.hasAssessment ? 'Assessment' : 'Mindfullness'})</option>
                            ))}
                        </select>
                        <button
                            onClick={handleAssignCourse}
                            style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 24px',
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                height: '48px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Assign
                        </button>
                    </div>
                    <div style={{
                        marginTop: '8px',
                        fontSize: '0.85rem',
                        color: '#64748b',
                        fontStyle: 'italic'
                    }}>
                        Direct assignment will give priority access to this specific user
                    </div>
                </div>

                {/* Available Courses List */}
                <div>
                    <h4 style={{
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        color: '#1e293b',
                        marginBottom: '16px'
                    }}>
                        Available Courses ({availableCourses.length})
                    </h4>

                    {isLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                            Loading courses...
                        </div>
                    ) : availableCourses.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {availableCourses.map((course) => (
                                <div
                                    key={course.id}
                                    style={{
                                        background: 'white',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        border: '1px solid #e5e7eb',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}
                                >
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            fontSize: '1.1rem',
                                            fontWeight: '600',
                                            color: '#1e293b',
                                            marginBottom: '8px'
                                        }}>
                                            {course.title}
                                        </div>
                                        {course.description && (
                                            <div style={{
                                                fontSize: '0.9rem',
                                                color: '#64748b',
                                                marginBottom: '8px'
                                            }}>
                                                {course.description}
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <span style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                background: (course.hasAssessment ? '#0ea5e9' : '#64748b'),
                                                color: 'white',
                                                padding: '4px 12px',
                                                borderRadius: '12px',
                                                fontSize: '0.8rem',
                                                fontWeight: '600'
                                            }}>
                                                {course.hasAssessment ? 'Assessment' : 'Mindfullness'}
                                            </span>
                                            <span style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                background: getSourceBadgeColor(course.assignmentSource),
                                                color: 'white',
                                                padding: '4px 12px',
                                                borderRadius: '12px',
                                                fontSize: '0.8rem',
                                                fontWeight: '600'
                                            }}>
                                                {getSourceIcon(course.assignmentSource)} {course.assignmentSource}
                                            </span>
                                            {course.sourceName && (
                                                <span style={{
                                                    fontSize: '0.85rem',
                                                    color: '#64748b'
                                                }}>
                                                    from {course.sourceName}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {course.assignmentSource === 'Direct' && (
                                        <button
                                            data-target="#removeUserCourseModal"
                                            data-toggle="modal"
                                            onClick={() => setCourseToRemove(course)}
                                            style={{
                                                background: '#ef4444',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                padding: '8px 16px',
                                                cursor: 'pointer',
                                                fontSize: '0.85rem',
                                                fontWeight: '600'
                                            }}
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{
                            background: 'white',
                            borderRadius: '12px',
                            padding: '40px',
                            border: '1px solid #e5e7eb',
                            textAlign: 'center',
                            color: '#64748b'
                        }}>
                            No courses assigned yet
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default UserCourseAssignments;
